import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ServiceType } from '@common/enums';
import {
  ConfigureServices,
  OdataObject,
  OdataServiceStatistics,
  OdataServiceStatisticsSave,
  ServiceStatistics,
  ServiceStatisticsSaveObject
} from '@common/models';
import { ODataServiceFactory } from '@common/odata';
import { AbilityService } from '@common/services/auth/ability.service';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private readonly ability = inject(AbilityService);
  private readonly authContext = inject(AuthContextService);
  private readonly http = inject(HttpClient);
  private readonly odataFactory = inject(ODataServiceFactory);

  private readonly backupMethods = this.odataFactory.CreateService<any>('BackupUsers');
  private readonly domainMethods = this.odataFactory.CreateService<any>('Domains');
  private readonly requestScopeUrl = this.odataFactory.CreateService<any>('RequestScopeUrl');

  protected readonly authUserId = this.authContext.id;
  protected readonly authDomainId = this.authContext.domainId;

  protected readonly isAdmin = this.ability.isAdmin;

  getServiceStatistics(): Observable<ServiceStatistics[]> {
    const methodName = 'ServiceStatistics';
    const isAdmin = this.isAdmin();
    const id = isAdmin ? this.authDomainId() : this.authUserId();
    const service = isAdmin ? this.domainMethods : this.backupMethods;

    return service.ItemProperty<OdataObject<OdataServiceStatistics[]>>(id, methodName).pipe(
      map((stats) => {
        if (stats == null) return [] as ServiceStatistics[];

        return stats.value.map((odataObj: OdataServiceStatistics) => new ServiceStatistics(odataObj));
      })
    );
  }

  configureServices(saveData: ServiceStatisticsSaveObject): Observable<ConfigureServices[] | null> {
    const isAdmin = this.isAdmin();
    const id = isAdmin ? this.authDomainId() : this.authUserId();
    const service = isAdmin ? this.domainMethods : this.backupMethods;
    const url = `${service.Query().GetUrl()}(${id})/ConfigureServices`;

    const postBody: OdataServiceStatisticsSave = {
      ApplyToEnabledUsers: saveData.forAllEnabledUsers,
      Services: saveData.services.map((s) => {
        return {
          Service: ServiceType[s.serviceType],
          Enabled: s.enabled
        };
      })
    };

    return this.http.post<OdataObject<ConfigureServices[] | null>>(url, postBody).pipe(map((res) => res.value));
  }

  getRequestScopeUrl(services: ServiceType[]): Observable<string> {
    return this.requestScopeUrl
      .Query()
      .CustomQueryOptions({ key: 'services', value: services.map((s) => ServiceType[s]).join(',') })
      .Exec() as unknown as Observable<string>;
  }
}
